import { periodMonth } from "./billingPeriods.js";

const BILLING_TABS = new Set(["billable", "invoices", "accounts", "reports", "export"]);
const BILLING_DIRECTORY_STATUSES = new Set(["all", "created", "needs_setup"]);
const BILLABLE_STATUSES = new Set(["", "eligible", "blocked"]);
const EXPORT_STATUSES = new Set(["", "eligible", "blocked", "allocated", "all"]);
const INVOICE_STATUSES = new Set(["", "draft", "issued", "sent", "overdue", "paid", "void"]);
export const BILLING_UI_SCHEMA_VERSION = 2;

function cleanString(value, fallback = "", maxLength = 500) {
  return typeof value === "string" ? value.slice(0, maxLength) : fallback;
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function previousMonthStartDate(referenceDate = new Date()) {
  const resolvedDate = referenceDate instanceof Date && Number.isFinite(referenceDate.getTime())
    ? referenceDate
    : new Date();
  return formatLocalDate(new Date(resolvedDate.getFullYear(), resolvedDate.getMonth() - 1, 1));
}

export function isValidBillingDateFilter(value) {
  if (value === "") return true;
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
}

function invoiceMonth(fromDate, toDate) {
  return periodMonth(fromDate, toDate) ? { fromDate, toDate } : { fromDate: "", toDate: "" };
}

function normalizeDateFilter(value, fallback = "") {
  return isValidBillingDateFilter(value) ? value : fallback;
}

export function createDefaultBillableFilters(referenceDate = new Date()) {
  return {
    search: "",
    clientId: "",
    billingSide: "",
    fromDate: previousMonthStartDate(referenceDate),
    toDate: "",
    status: "eligible",
    blockingReason: "",
    overdueOnly: false,
  };
}

/** Filtres de départ d'« À facturer » (« Prêts » depuis le 1er du mois précédent) : rien à effacer. */
export function isDefaultBillableFilters(filters, referenceDate = new Date()) {
  return Object.entries(createDefaultBillableFilters(referenceDate)).every(([key, value]) => (filters?.[key] ?? "") === value)
    && !filters?.clientName && !filters?.billingAccountId;
}

// Relevés : tous les mois par défaut ; un filtre de dates couvre toujours un mois entier.
export function createDefaultInvoiceFilters() {
  return {
    search: "",
    clientId: "",
    clientName: "",
    billingAccountId: "",
    billingSide: "",
    fromDate: "",
    toDate: "",
    status: "",
  };
}

export function createDefaultAccountsFilters() {
  return { search: "", subTab: "accounts", directoryStatus: "all", directoryType: "all" };
}

export function createDefaultReportFilters(referenceDate = new Date()) {
  return {
    search: "",
    clientId: "",
    billingAccountId: "",
    billingSide: "",
    fromDate: previousMonthStartDate(referenceDate),
    toDate: "",
    status: "",
  };
}

export function createDefaultExportFilters(referenceDate = new Date()) {
  return {
    search: "",
    clientId: "",
    billingAccountId: "",
    billingSide: "",
    fromDate: previousMonthStartDate(referenceDate),
    toDate: "",
    status: "eligible",
  };
}

export function createDefaultBillingPreferences(referenceDate = new Date()) {
  return {
    schemaVersion: BILLING_UI_SCHEMA_VERSION,
    lastTab: "billable",
    billableFilters: createDefaultBillableFilters(referenceDate),
    invoiceFilters: createDefaultInvoiceFilters(),
    accountsFilters: createDefaultAccountsFilters(),
    reportFilters: createDefaultReportFilters(referenceDate),
    exportFilters: createDefaultExportFilters(referenceDate),
    expandedGroups: {
      accountKeys: [],
      clientKeys: [],
      sideKeys: [],
    },
  };
}

function normalizeFilterSource(payload, camelKey, snakeKey) {
  const value = payload?.[camelKey] ?? payload?.[snakeKey];
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeGroupKeys(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((entry) => typeof entry === "string" && entry.length > 0).map((entry) => entry.slice(0, 512)))].slice(0, 2000);
}

export function normalizeBillingPreferences(payload, { referenceDate = new Date() } = {}) {
  const fallback = createDefaultBillingPreferences(referenceDate);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return fallback;

  const billable = normalizeFilterSource(payload, "billableFilters", "billable_filters");
  const invoices = normalizeFilterSource(payload, "invoiceFilters", "invoice_filters");
  const accounts = normalizeFilterSource(payload, "accountsFilters", "accounts_filters");
  const reports = normalizeFilterSource(payload, "reportFilters", "report_filters");
  const exports = normalizeFilterSource(payload, "exportFilters", "export_filters");
  const expanded = normalizeFilterSource(payload, "expandedGroups", "expanded_groups");
  const lastTab = payload.lastTab ?? payload.last_tab;
  const savedDirectoryStatus = accounts.directoryStatus ?? accounts.directory_status;
  const directoryType = accounts.directoryType ?? accounts.directory_type;
  const directoryStatus = savedDirectoryStatus === "meuneries" ? "all" : ["associated", "unlinked"].includes(savedDirectoryStatus) ? "created" : savedDirectoryStatus;
  const rawSchemaVersion = Number(payload.schemaVersion ?? payload.schema_version);
  let schemaVersion = Number.isInteger(rawSchemaVersion) && rawSchemaVersion >= 1 ? rawSchemaVersion : 1;
  let billableStatus = cleanString(billable.status);
  const invoiceStatus = cleanString(invoices.status);
  const reportStatus = cleanString(reports.status);
  const exportStatus = cleanString(exports.status);

  if (!BILLABLE_STATUSES.has(billableStatus)) {
    billableStatus = schemaVersion < BILLING_UI_SCHEMA_VERSION ? "" : fallback.billableFilters.status;
  }
  if (schemaVersion < BILLING_UI_SCHEMA_VERSION) {
    if (billableStatus === "") {
      billableStatus = "eligible";
    }
    schemaVersion = BILLING_UI_SCHEMA_VERSION;
  }

  return {
    schemaVersion,
    lastTab: BILLING_TABS.has(lastTab) ? lastTab : fallback.lastTab,
    billableFilters: {
      search: cleanString(billable.search),
      clientId: cleanString(billable.clientId ?? billable.client_id, "", 255),
      billingSide: "",
      fromDate: normalizeDateFilter(billable.fromDate ?? billable.from_date, fallback.billableFilters.fromDate),
      toDate: normalizeDateFilter(billable.toDate ?? billable.to_date, ""),
      status: billableStatus,
      blockingReason: cleanString(billable.blockingReason ?? billable.blocking_reason, "", 255),
      overdueOnly: typeof (billable.overdueOnly ?? billable.overdue_only) === "boolean"
        ? (billable.overdueOnly ?? billable.overdue_only)
        : false,
    },
    invoiceFilters: {
      search: cleanString(invoices.search),
      clientId: cleanString(invoices.clientId ?? invoices.client_id, "", 255),
      clientName: cleanString(invoices.clientName ?? invoices.client_name, "", 255),
      billingAccountId: cleanString(invoices.billingAccountId ?? invoices.billing_account_id, "", 255),
      billingSide: "",
      ...invoiceMonth(invoices.fromDate ?? invoices.from_date, invoices.toDate ?? invoices.to_date),
      status: INVOICE_STATUSES.has(invoiceStatus) ? invoiceStatus : "",
    },
    accountsFilters: {
      search: cleanString(accounts.search),
      directoryStatus: BILLING_DIRECTORY_STATUSES.has(directoryStatus) ? directoryStatus : "all",
      directoryType: ["all", "coops"].includes(directoryType) ? directoryType : "all",
      subTab: "accounts",
    },
    reportFilters: {
      search: cleanString(reports.search),
      clientId: cleanString(reports.clientId ?? reports.client_id, "", 255),
      billingAccountId: cleanString(reports.billingAccountId ?? reports.billing_account_id, "", 255),
      billingSide: "",
      fromDate: normalizeDateFilter(reports.fromDate ?? reports.from_date, fallback.reportFilters.fromDate),
      toDate: normalizeDateFilter(reports.toDate ?? reports.to_date, ""),
      status: BILLABLE_STATUSES.has(reportStatus) ? reportStatus : "",
    },
    exportFilters: {
      search: cleanString(exports.search),
      clientId: cleanString(exports.clientId ?? exports.client_id, "", 255),
      billingAccountId: cleanString(exports.billingAccountId ?? exports.billing_account_id, "", 255),
      billingSide: "",
      fromDate: normalizeDateFilter(exports.fromDate ?? exports.from_date, fallback.exportFilters.fromDate),
      toDate: normalizeDateFilter(exports.toDate ?? exports.to_date, ""),
      status: EXPORT_STATUSES.has(exportStatus) ? exportStatus : fallback.exportFilters.status,
    },
    expandedGroups: {
      accountKeys: normalizeGroupKeys(expanded.accountKeys ?? expanded.account_keys),
      clientKeys: normalizeGroupKeys(expanded.clientKeys ?? expanded.client_keys),
      sideKeys: normalizeGroupKeys(expanded.sideKeys ?? expanded.side_keys),
    },
  };
}

export function toBillingPreferencesPayload(preferences) {
  const normalized = normalizeBillingPreferences(preferences);
  return {
    schema_version: normalized.schemaVersion,
    last_tab: normalized.lastTab,
    billable_filters: {
      search: normalized.billableFilters.search,
      client_id: normalized.billableFilters.clientId,
      billing_side: normalized.billableFilters.billingSide,
      from_date: normalized.billableFilters.fromDate,
      to_date: normalized.billableFilters.toDate,
      status: normalized.billableFilters.status,
      blocking_reason: normalized.billableFilters.blockingReason,
      overdue_only: normalized.billableFilters.overdueOnly,
    },
    invoice_filters: {
      search: normalized.invoiceFilters.search,
      client_id: normalized.invoiceFilters.clientId,
      client_name: normalized.invoiceFilters.clientName,
      billing_account_id: normalized.invoiceFilters.billingAccountId,
      billing_side: normalized.invoiceFilters.billingSide,
      from_date: normalized.invoiceFilters.fromDate,
      to_date: normalized.invoiceFilters.toDate,
      status: normalized.invoiceFilters.status,
    },
    accounts_filters: {
      search: normalized.accountsFilters.search,
      sub_tab: normalized.accountsFilters.subTab,
      directory_status: normalized.accountsFilters.directoryStatus,
      directory_type: normalized.accountsFilters.directoryType,
    },
    report_filters: {
      search: normalized.reportFilters.search,
      client_id: normalized.reportFilters.clientId,
      billing_account_id: normalized.reportFilters.billingAccountId,
      billing_side: normalized.reportFilters.billingSide,
      from_date: normalized.reportFilters.fromDate,
      to_date: normalized.reportFilters.toDate,
      status: normalized.reportFilters.status,
    },
    export_filters: {
      search: normalized.exportFilters.search,
      client_id: normalized.exportFilters.clientId,
      billing_account_id: normalized.exportFilters.billingAccountId,
      billing_side: normalized.exportFilters.billingSide,
      from_date: normalized.exportFilters.fromDate,
      to_date: normalized.exportFilters.toDate,
      status: normalized.exportFilters.status,
    },
    expanded_groups: {
      account_keys: normalized.expandedGroups.accountKeys,
      client_keys: normalized.expandedGroups.clientKeys,
      side_keys: normalized.expandedGroups.sideKeys,
    },
  };
}
